import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import builtForShopifyBadge from "../assets/built-for-shopify-media-kit-2025/Built for Shopify badge/PNG/badge-built-for-shopify-light.png";
import "./Header.css";

export default function Header() {
  const location = useLocation();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [toolsOpen, setToolsOpen] = useState(false); // single source of truth (desktop + mobile)
  const [resourcesOpen, setResourcesOpen] = useState(false);
  const toolsDesktopRef = useRef(null);
  const toolsMobileRef = useRef(null);
  const resourcesDesktopRef = useRef(null);
  const resourcesMobileRef = useRef(null);

  const isActive = (path) => location.pathname === path;

  // Close drawer & submenu when route changes
  useEffect(() => {
    setDrawerOpen(false);
    setToolsOpen(false);
    setResourcesOpen(false);
  }, [location.pathname]);

  // Outside click to close submenu (but it won't vanish on hover)
  useEffect(() => {
    function onDocMouseDown(e) {
      const d = toolsDesktopRef.current;
      const m = toolsMobileRef.current;
      const r = resourcesDesktopRef.current;
      const rm = resourcesMobileRef.current;
      const insideDesktop = d && d.contains(e.target);
      const insideMobile = m && m.contains(e.target);
      const insideResources = r && r.contains(e.target);
      const insideResourcesMobile = rm && rm.contains(e.target);
      if (!insideDesktop && !insideMobile) setToolsOpen(false);
      if (!insideResources && !insideResourcesMobile) setResourcesOpen(false);
    }
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, []);

  const toggleDrawer = () => {
    setDrawerOpen((v) => !v);
    if (toolsOpen) setToolsOpen(false);
    if (resourcesOpen) setResourcesOpen(false);
  };

  const handleTopLink = () => {
    if (drawerOpen) setDrawerOpen(false);
    if (toolsOpen) setToolsOpen(false);
    if (resourcesOpen) setResourcesOpen(false);
  };

  const onMenuButtonKeyDown = (e, open, setOpen) => {
    const menu = document.getElementById(e.currentTarget.getAttribute("aria-controls"));
    const links = menu ? Array.from(menu.querySelectorAll("a")) : [];
    if (["Enter", " ", "ArrowDown", "ArrowUp"].includes(e.key)) {
      e.preventDefault();
      const opening = e.key.startsWith("Arrow") || !open;
      setOpen(opening);
      if (opening) {
        const index = e.key === "ArrowUp" ? links.length - 1 : 0;
        setTimeout(() => links[index]?.focus(), 0);
      }
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  const onMenuLinkKeyDown = (e, index, setOpen) => {
    const menu = e.currentTarget.closest("ul");
    const links = Array.from(menu.querySelectorAll("a"));
    const targets = {
      ArrowDown: (index + 1) % links.length,
      ArrowUp: (index + links.length - 1) % links.length,
      Home: 0,
      End: links.length - 1,
    };
    if (e.key in targets) {
      e.preventDefault();
      links[targets[e.key]]?.focus();
    } else if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
      document.querySelector(`[aria-controls="${menu.id}"]`)?.focus();
    }
  };
  const onResourcesButtonKeyDown = (e) => onMenuButtonKeyDown(e, resourcesOpen, setResourcesOpen);
  const onResourcesSubmenuKeyDown = (e, index) => onMenuLinkKeyDown(e, index, setResourcesOpen);
  const onToolsButtonKeyDown = (e) => onMenuButtonKeyDown(e, toolsOpen, setToolsOpen);
  const onSubmenuKeyDown = (e, index) => onMenuLinkKeyDown(e, index, setToolsOpen);

  return (
    <header className="header">
      <div className="header__inner">
        {/* Brand / Logo */}
        <div className="header__brand">
          <Link to="/" onClick={handleTopLink} className="header__brandLink">
            <img
              className="header__logo"
              src="/logo.png"
              alt="Clean Sweeps"
            />
            <span>Clean Sweeps</span>
          </Link>
          <Link
            to={{ pathname: "/", hash: "#built-for-shopify" }}
            onClick={handleTopLink}
            className="header__bfsLink"
            aria-label="Built for Shopify"
          >
            <img
              className="header__bfsBadge"
              src={builtForShopifyBadge}
              alt="Built for Shopify badge"
            />
          </Link>
        </div>

        {/* Desktop navigation */}
        <nav className="header__nav" aria-label="Primary">
          <ul>
            <li className={isActive("/") ? "active" : ""}>
              <Link to="/" onClick={handleTopLink}>
                Home
              </Link>
            </li>

            <li
              className={`has-submenu ${resourcesOpen ? "is-open" : ""}`}
              ref={resourcesDesktopRef}
            >
              <button
                type="button"
                className="submenu-toggle"
                aria-expanded={resourcesOpen ? "true" : "false"}
                aria-controls="resources-submenu"
                onClick={() => setResourcesOpen((o) => !o)}
                onKeyDown={onResourcesButtonKeyDown}
              >
                Resources
                <span className="submenu-caret" aria-hidden="true">
                  ▾
                </span>
              </button>

              <ul
                id="resources-submenu"
                className="submenu"
                hidden={!resourcesOpen}
              >
                <li
                  className={
                    isActive("/how-to-run-a-sweepstakes") ? "active" : ""
                  }
                >
                  <Link
                    to="/how-to-run-a-sweepstakes"

                    onKeyDown={(e) => onResourcesSubmenuKeyDown(e, 0)}
                    onClick={handleTopLink}
                  >
                    How to Run a Sweepstakes
                  </Link>
                </li>
                <li
                  className={
                    isActive("/sweepstakes-entry-widgets") ? "active" : ""
                  }
                >
                  <Link
                    to="/sweepstakes-entry-widgets"

                    onKeyDown={(e) => onResourcesSubmenuKeyDown(e, 1)}
                    onClick={handleTopLink}
                  >
                    Sweepstakes Widgets
                  </Link>
                </li>
                <li
                  className={
                    isActive("/sweepstakes-compliance-checklist") ? "active" : ""
                  }
                >
                  <Link
                    to="/sweepstakes-compliance-checklist"

                    onKeyDown={(e) => onResourcesSubmenuKeyDown(e, 2)}
                    onClick={handleTopLink}
                  >
                    Compliance Checklist
                  </Link>
                </li>
                <li
                  className={
                    isActive("/dropshipping-sweepstakes") ? "active" : ""
                  }
                >
                  <Link
                    to="/dropshipping-sweepstakes"

                    onKeyDown={(e) => onResourcesSubmenuKeyDown(e, 3)}
                    onClick={handleTopLink}
                  >
                    Dropshipping Sweepstakes
                  </Link>
                </li>
                <li className={location.pathname.startsWith("/api-documentation") ? "active" : ""}>
                  <Link to="/api-documentation" onClick={handleTopLink}
                    onKeyDown={(e) => onResourcesSubmenuKeyDown(e, 4)}>
                    API Documentation
                  </Link>
                </li>
              </ul>
            </li>
            <li>
              <a href="/#pricing" onClick={handleTopLink}>
                Pricing
              </a>
            </li>
            <li className={isActive("/setup") ? "active" : ""}>
              <Link to="/setup" onClick={handleTopLink}>
                Setup Guides
              </Link>
            </li>
            <li className={isActive("/consulting") ? "active" : ""}>
              <a
                href="https://apps.shopify.com/clean-sweeps"
                onClick={handleTopLink}
                className="header__cta"
              >
                Get Started
              </a>
            </li>

            {/* Tools (Disclosure dropdown) */}
            <li
              className={`has-submenu ${toolsOpen ? "is-open" : ""}`}
              ref={toolsDesktopRef}
            >
              <button
                type="button"
                className="submenu-toggle"
                aria-expanded={toolsOpen ? "true" : "false"}
                aria-controls="tools-submenu"
                onClick={() => setToolsOpen((o) => !o)}
                onKeyDown={onToolsButtonKeyDown}
              >
                Tools
                <span className="submenu-caret" aria-hidden="true">
                  ▾
                </span>
              </button>

              <ul id="tools-submenu" className="submenu" hidden={!toolsOpen}>
                <li className={isActive("/winner-select") ? "active" : ""}>
                  <Link
                    to="/winner-select"

                    onKeyDown={(e) => onSubmenuKeyDown(e, 0)}
                    onClick={handleTopLink}
                  >
                    Winner Select
                  </Link>
                </li>

                <li className={isActive("/convert") ? "active" : ""}>
                  <Link
                    to="/convert"

                    onKeyDown={(e) => onSubmenuKeyDown(e, 1)}
                    onClick={handleTopLink}
                  >
                    Convert Entrants CSV
                  </Link>
                </li>
              </ul>
            </li>
          </ul>
        </nav>

        {/* Mobile hamburger */}
        <button
          className={`header__burger ${drawerOpen ? "is-open" : ""}`}
          aria-label="Toggle menu"
          aria-expanded={drawerOpen ? "true" : "false"}
          onClick={toggleDrawer}
        >
          <span />
          <span />
          <span />
        </button>
      </div>

      {/* Mobile drawer */}
      <div className={`header__drawer ${drawerOpen ? "open" : ""}`}>
        <ul className="header__drawerList">
          <li className={isActive("/") ? "active" : ""}>
            <Link to="/" onClick={handleTopLink}>
              Home
            </Link>
          </li>

          <li
            className={`has-submenu ${resourcesOpen ? "is-open" : ""}`}
            ref={resourcesMobileRef}
          >
            <button
              type="button"
              className="submenu-toggle"
              aria-expanded={resourcesOpen ? "true" : "false"}
              aria-controls="resources-submenu-mobile"
              onClick={() => setResourcesOpen((o) => !o)}
              onKeyDown={onResourcesButtonKeyDown}
            >
              Resources
              <span className="submenu-caret" aria-hidden="true">
                ▾
              </span>
            </button>

            <ul
              id="resources-submenu-mobile"
              className="submenu submenu--mobile"
              hidden={!resourcesOpen}
            >
              <li
                className={
                  isActive("/how-to-run-a-sweepstakes") ? "active" : ""
                }
              >
                <Link
                  to="/how-to-run-a-sweepstakes"

                  onKeyDown={(e) => onResourcesSubmenuKeyDown(e, 0)}
                  onClick={handleTopLink}
                >
                  How to Run a Sweepstakes
                </Link>
              </li>
              <li
                className={
                  isActive("/sweepstakes-entry-widgets") ? "active" : ""
                }
              >
                <Link
                  to="/sweepstakes-entry-widgets"

                  onKeyDown={(e) => onResourcesSubmenuKeyDown(e, 1)}
                  onClick={handleTopLink}
                >
                  Sweepstakes Widgets
                </Link>
              </li>
              <li
                className={
                  isActive("/sweepstakes-compliance-checklist") ? "active" : ""
                }
              >
                <Link
                  to="/sweepstakes-compliance-checklist"

                  onKeyDown={(e) => onResourcesSubmenuKeyDown(e, 2)}
                  onClick={handleTopLink}
                >
                  Compliance Checklist
                </Link>
              </li>
              <li
                className={
                  isActive("/dropshipping-sweepstakes") ? "active" : ""
                }
              >
                <Link
                  to="/dropshipping-sweepstakes"

                  onKeyDown={(e) => onResourcesSubmenuKeyDown(e, 3)}
                  onClick={handleTopLink}
                >
                  Dropshipping Sweepstakes
                </Link>
              </li>
                <li className={location.pathname.startsWith("/api-documentation") ? "active" : ""}>
                  <Link to="/api-documentation" onClick={handleTopLink}
                    onKeyDown={(e) => onResourcesSubmenuKeyDown(e, 4)}>
                    API Documentation
                  </Link>
                </li>
            </ul>
          </li>
          <li>
            <a href="/#pricing" onClick={handleTopLink}>
              Pricing
            </a>
          </li>
          <li className={isActive("/setup") ? "active" : ""}>
            <Link to="/setup" onClick={handleTopLink}>
              Setup Guides
            </Link>
          </li>
          <li className={isActive("/consulting") ? "active" : ""}>
            <a
              href="https://apps.shopify.com/clean-sweeps"
              onClick={handleTopLink}
              className="header__cta"
            >
              Get Started
            </a>
          </li>

          {/* Tools mirrored in mobile */}
          <li
            className={`has-submenu ${toolsOpen ? "is-open" : ""}`}
            ref={toolsMobileRef}
          >
            <button
              type="button"
              className="submenu-toggle"
              aria-expanded={toolsOpen ? "true" : "false"}
              aria-controls="tools-submenu-mobile"
              onClick={() => setToolsOpen((o) => !o)}
              onKeyDown={onToolsButtonKeyDown}
            >
              Tools
              <span className="submenu-caret" aria-hidden="true">
                ▾
              </span>
            </button>

            <ul
              id="tools-submenu-mobile"
              className="submenu submenu--mobile"
              hidden={!toolsOpen}
            >
              <li className={isActive("/winner-select") ? "active" : ""}>
                <Link
                  to="/winner-select"

                  onKeyDown={(e) => onSubmenuKeyDown(e, 0)}
                  onClick={handleTopLink}
                >
                  Winner Select
                </Link>
              </li>

              <li className={isActive("/convert") ? "active" : ""}>
                <Link
                  to="/convert"

                  onKeyDown={(e) => onSubmenuKeyDown(e, 1)}
                  onClick={handleTopLink}
                >
                  Convert Entrants CSV
                </Link>
              </li>
            </ul>
          </li>
        </ul>
      </div>
    </header>
  );
}
